import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Slider,
    TextField,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Point, GlobalContext } from '../../../context/context';
import L from 'leaflet';
import { handleCreateIsochrone } from '../handlers/isochroneHandlers';

interface IsochroneModalProps {
    open: boolean;
    onClose: () => void;
    point: Point | null;
    global: GlobalContext;
    mapRef: React.MutableRefObject<L.Map | null>;
    setIsochroneLoading: (loading: boolean) => void;
    setIsochroneError: (error: string | null) => void;
    setIsIsochroneModalOpen: (open: boolean) => void;
    setIsochronePoint: (point: Point | null) => void;
    loading?: boolean;
    error?: string | null;
}

export default function IsochroneModal({
    open,
    onClose,
    point,
    global,
    mapRef,
    setIsochroneLoading,
    setIsochroneError,
    setIsIsochroneModalOpen,
    setIsochronePoint,
    loading = false,
    error
}: IsochroneModalProps) {
    const { t } = useTranslation(['map', 'common']);
    const MODES = [
        { value: 'walking', label: t('modals.isochrone.modeWalking') },
        { value: 'driving', label: t('modals.isochrone.modeDriving') },
        { value: 'cycling', label: t('modals.isochrone.modeCycling') },
        { value: 'transit', label: t('modals.isochrone.modeTransit') },
    ];
    const [mode, setMode] = useState('walking');
    const [time, setTime] = useState(15);
    const [color, setColor] = useState('#1976d2');

    const handleSubmit = () => {
        if (point) {
            handleCreateIsochrone({
                point,
                mode,
                time,
                color,
                global,
                mapRef,
                setIsochroneLoading,
                setIsochroneError,
                setIsIsochroneModalOpen,
                setIsochronePoint
            });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t('modals.isochrone.title')}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={3} mt={1}>
                    <Typography variant="body2" color="text.secondary">
                        {t('modals.isochrone.description')}
                    </Typography>
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                    <FormControl fullWidth disabled={loading}>
                        <InputLabel id="isochrone-mode-label">{t('modals.isochrone.modeLabel')}</InputLabel>
                        <Select
                            labelId="isochrone-mode-label"
                            value={mode}
                            label={t('modals.isochrone.modeLabel')}
                            onChange={e => setMode(e.target.value)}
                        >
                            {MODES.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box>
                        <Typography gutterBottom>{t('modals.isochrone.timeLabel')}</Typography>
                        <Slider
                            value={time}
                            min={5}
                            max={60}
                            step={1}
                            valueLabelDisplay="auto"
                            onChange={(_, v) => setTime(v as number)}
                            disabled={loading}
                        />
                    </Box>
                    <TextField
                        label={t('modals.isochrone.colorLabel')}
                        type="color"
                        value={color}
                        onChange={e => setColor(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={loading}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={loading}>{t('common:cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!point || loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {loading ? t('modals.isochrone.creating') : t('modals.isochrone.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
} 